import React, { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import api from '../../api/client';
import { toast } from '../../hooks/use-toast';
import EmailSignatureEditor from './EmailSignatureEditor';

/**
 * Wave 1u.1 — Universal signature editor.
 *
 * Reuses the legacy `EmailSignatureEditor` (RichText + image upload + width
 * slider + preview) but persists to `db.user_signatures` so the signature
 * applies to ALL providers (Gmail / Microsoft / SMTP).
 */
export default function UniversalSignatureEditor() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [imageDirty, setImageDirty] = useState(false);
  const [form, setForm] = useState({
    signature_html: '',
    signature_image_b64: '',
    signature_image_mime: 'image/png',
    signature_image_width: 320,
    signature_image_set: false,
  });
  const [signaturePreview, setSignaturePreview] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/user/signature/raw');
      setForm({
        signature_html: data?.signature_html || '',
        signature_image_b64: '',  // not returned to FE
        signature_image_mime: data?.signature_image_mime || 'image/png',
        signature_image_width: data?.signature_image_width || 320,
        signature_image_set: !!data?.signature_image_set,
      });
      if (data?.signature_image_set) {
        try {
          const blob = await api.get('/user/signature/image', { responseType: 'blob' });
          setSignaturePreview(URL.createObjectURL(blob.data));
        } catch { /* silent */ }
      } else {
        setSignaturePreview(null);
      }
      setImageDirty(false);
    } catch (err) {
      console.warn('[signature load]', err);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  // Wrap setForm to detect image changes (so we know whether to send the b64)
  const setFormTracked = (updater) => {
    setForm((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (next.signature_image_b64 !== prev.signature_image_b64) setImageDirty(true);
      if (next.signature_image_set !== prev.signature_image_set) setImageDirty(true);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        signature_html: form.signature_html || '',
        signature_image_width: Number(form.signature_image_width || 320),
      };
      if (imageDirty) {
        if (form.signature_image_set && form.signature_image_b64) {
          // Strip "data:image/...;base64," prefix if present
          payload.signature_image_b64 = String(form.signature_image_b64).split('base64,', 2)[1] || form.signature_image_b64;
          payload.signature_image_mime = form.signature_image_mime || 'image/png';
        } else {
          payload.signature_image_b64 = '';  // explicit removal
        }
      }
      await api.put('/user/signature', payload);
      toast({ title: 'Signature enregistrée', description: 'Appliquée à tous vos envois (Gmail, Outlook, SMTP).' });
      reload();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    } finally { setSaving(false); }
  };

  return (
    <div className="soft-card p-5" data-testid="universal-signature-editor">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-[15px] font-bold text-[#1A1B2E]">Signature email universelle</h3>
          <p className="text-[12px] text-[#6B6C85]">
            Appliquée automatiquement à <strong>tous vos envois</strong> — Gmail, Microsoft Outlook, SMTP.
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving || loading}
          data-testid="universal-signature-save"
          className="h-9 px-4 rounded-lg brand-gradient text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Enregistrer
        </button>
      </div>

      {loading ? (
        <div className="py-6 text-center text-[#8E8FA5] text-[12.5px]"><Loader2 className="w-4 h-4 animate-spin inline-block mr-1" /> Chargement…</div>
      ) : (
        <EmailSignatureEditor
          form={form}
          setForm={setFormTracked}
          signaturePreview={signaturePreview}
          setSignaturePreview={(v) => { setSignaturePreview(v); setImageDirty(true); }}
        />
      )}
    </div>
  );
}
